import chroma from 'chroma-js';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithStorage } from 'jotai/utils';
import {
  Grid,
  GunBarrelView,
  PreDrillWorkspaceRef,
  Profile2D,
  StructureGridCollection,
  Well3DViewerResponse,
} from 'types/api-responses';

// Interfaces
export interface TrajectoryPoint {
  x: number;
  y: number;
  z: number;
}

export interface WellBoreData {
  id: string;
  uwi14: string;
  uwi12: string;
  /** Bore CLASSIFICATION ('producing' | 'sidetrack' | 'pre_drill' | ...), plus
   *  the FE-internal 'estimated' line-kind used for rendering. `type` alone no
   *  longer disambiguates a synthesized vertical from a real producing bore at
   *  the API boundary — see `estimated`. */
  type: string;
  /** True for an estimated (synthesized vertical) bore — the provenance signal
   *  driving dashed/semi-transparent styling across the 3D viewer, gun barrel,
   *  2D Profile, and inset. */
  estimated: boolean;
  spudDate: string | null;
  geometryLabel?: string;
  trajectory: TrajectoryPoint[];
  profile2D?: Profile2D;
  gunBarrelView?: GunBarrelView;
  /** Pre-drill and estimated bores: the workspace driving this geometry (name/href
   *  used by the Wells config panel's workspace label + edit pencil). */
  workspace?: PreDrillWorkspaceRef | null;
}

export interface WellData {
  uwi: string;
  name: string | null;
  source: string | null;
  geometrySource: string;
  color: string;
  currentOperator: string;
  status: string;
  /** HORIZONTAL / VERTICAL / DIRECTIONAL / null — drives the 2D Profile's
   *  vertical-well handling (the lateral-focused zoom + heel/toe assumptions
   *  don't apply to a vertical bore) and the gun barrel's vertical sticks.
   *  Carried here because this response is radius-scoped, so it covers OFFSET
   *  wells; the well-map query is subject-only. */
  holeDirection: string | null;
  wellBores: WellBoreData[];
}

/**
 * Whether a hole direction is vertical. One home for the comparison so the 2D
 * Profile's focus window, the gun barrel's vertical-stick series, and the
 * marker builder can't drift apart — and so the casing rule lives in one place.
 * Compared case-insensitively: the wells query emits `VERTICAL`, but
 * `holeDirection` values elsewhere in the app appear as `Horizontal` /
 * `horizontal` too, so an exact-match `=== 'VERTICAL'` is a latent trap.
 * @param {string | null | undefined} holeDirection - Well-level hole direction.
 * @return {boolean} True when the well is vertical.
 */
export const isVerticalHoleDirection = (
  holeDirection: string | null | undefined
): boolean => (holeDirection ?? '').toUpperCase() === 'VERTICAL';

// Bores shown by default (before any toggle): a real producing bore and the
// pre-drill permit. A sidetrack is hidden by default (gated behind Show
// Sidetracks) — keyed on the bore's TYPE, so a survey-less/estimated sidetrack
// is treated like any other sidetrack rather than shown by default. Any other
// estimated bore falls back to visible.
export const isDefaultVisibleBore = (bore: {
  type: string;
  estimated?: boolean;
}): boolean =>
  bore.type === 'producing' ||
  bore.type === 'pre_drill' ||
  (!!bore.estimated && bore.type !== 'sidetrack');

// An estimated bore belonging to an OFFSET (non-subject) well. Estimated
// geometry is fetched radius-wide to feed the gun barrel's perforation overlay,
// so offset estimated bores reach the client; the 2D Profile must neither draw
// nor frame them (only the subject's own estimated stick belongs there). One
// named predicate keeps the two 2D-Profile call sites from drifting apart.
export const isOffsetEstimatedBore = (
  bore: { estimated?: boolean },
  wellUwi: string,
  sourceUwi: string
): boolean => !!bore.estimated && wellUwi !== sourceUwi;

/**
 * Whether a well survives the Show Vertical Wells toggle.
 *
 * Deliberately a WELL-level predicate rather than an extra argument to
 * {@link isBoreVisible}: hole direction is an attribute of the well, not of an
 * individual bore, and every bore of a vertical well is vertical. Threading it
 * through the bore-level predicate would imply a per-bore decision that does
 * not exist.
 *
 * Applies to the SUBJECT as well as to offsets — full parity with Show Permits,
 * which is bore-type-only and exempts nothing. An earlier version spared the
 * subject to avoid an empty chart, but that made this control behave unlike
 * every other toggle in the panel: on a vertical subject's own page, Show
 * Permits hid the well (its estimated bore is `type === 'estimated'`) while this
 * one did not. Switching a vertical subject off now empties its stick from the
 * chart, which is the same thing the other toggles already do.
 *
 * @param {object} well - The well, with its optional `holeDirection`.
 * @param {boolean} showVerticalWells - Current toggle state.
 * @return {boolean} True when the well should render.
 */
export const isWellVisibleForVerticalToggle = (
  well: { holeDirection?: string | null },
  showVerticalWells: boolean
): boolean => showVerticalWells || !isVerticalHoleDirection(well.holeDirection);

// Bore visibility for the left-panel toggles, keyed on BORE TYPE (not
// provenance): a producing bore is always shown, a sidetrack is gated by Show
// Sidetracks, and a pre-drill permit by Show Permits. Provenance (estimated) is
// the tie-breaker for any other bore — a survey-less sidetrack still obeys Show
// Sidetracks like a drilled one, while an estimated bore with no classified type
// follows Show Permits.
export const isBoreVisible = (
  bore: { type: string; estimated?: boolean },
  showSidetracks: boolean,
  showPermits: boolean
): boolean => {
  if (bore.type === 'pre_drill') return showPermits;
  if (bore.type === 'producing') return true;
  if (bore.type === 'sidetrack') return showSidetracks;
  return bore.estimated ? showPermits : showSidetracks;
};

export const wellSelectorMapConfigAtom = atomWithStorage(
  // Key bumped to -v2 (INS-8658) so the new "Main Wells off by default" applies
  // to existing users whose previous config persisted wellsV2.checked = true.
  'well-selector-map-config-v2',
  getMapDefaultConfig({
    wells: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: true,
        survey: true,
        vertical: false,
      },
    },
    wellsV2: {
      // Main Wells off by default on the inset (PRD AC 5.1). The wells-v2 source
      // is still mounted in WellSelectorMap so the Story-2 offset highlight works.
      checked: false,
      options: {
        surfaceHole: true,
        bottomHole: true,
        stick: true,
        survey: true,
        vertical: true,
        horizontal: true,
        horizontalPermit: true,
        horizontalDuc: true,
        horizontalProducing: true,
        horizontalShutIn: true,
        horizontalExpiredPermit: true,
        horizontalInactive: true,
        horizontalPrePermit: true,
        horizontalApd: true,
        horizontalProposal: true,
        horizontalPlanned: true,
        verticalPermit: true,
        verticalDuc: true,
        verticalProducing: true,
        verticalShutIn: true,
        verticalExpiredPermit: true,
        verticalInactive: true,
        verticalPrePermit: true,
        verticalApd: true,
        verticalProposal: true,
        verticalPlanned: true,
        permit: true,
        duc: true,
        producing: true,
        shutIn: true,
        expiredPermit: true,
        inactive: true,
        prePermit: true,
        apd: true,
        proposal: true,
        planned: true,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    titleEntityAssets: {
      checked: false,
      options: {
        surfaceInterest: false,
        workingInterest: false,
        unleased: false,
        royaltyInterest: false,
      },
    },
    basinTier: {
      checked: false,
      options: {
        basin: false,
        tier: false,
      },
    },
    scrollZoom: {
      checked: false,
    },
    popups: {
      checked: false,
    },
  })
);

// Color By Attribute
export type ColorByAttribute = 'well_status' | 'operator' | 'landing_zone';

export const COLOR_BY_OPTIONS: { id: ColorByAttribute; label: string }[] = [
  { id: 'well_status', label: 'Well Status' },
  { id: 'operator', label: 'Operator' },
  { id: 'landing_zone', label: 'Landing Zone' },
];

// Color utilities
export const WELL_COLORS = [
  '#CC3333', // Darker Red
  '#2A9D8F', // Darker Teal
  '#264653', // Darker Blue/Navy
  '#6A994E', // Darker Green
  '#ff69b4', // Hot Pink (permit color)
  '#fe9929', // Orange (duc color)
  '#4292c6', // Blue (producing color)
  '#555555', // Dark Gray (shut in color)
  '#969696', // Gray (expired permit color)
  '#8B4513', // Saddle Brown
  '#9932CC', // Dark Orchid
  '#FF6347', // Tomato
  '#4682B4', // Steel Blue
  '#32CD32', // Lime Green
  '#FF1493', // Deep Pink
  '#00CED1', // Dark Turquoise
  '#FFD700', // Gold
  '#DC143C', // Crimson
  '#00FA9A', // Medium Spring Green
  '#8A2BE2', // Blue Violet
  '#FF4500', // Orange Red
  '#2E8B57', // Sea Green
  '#B22222', // Fire Brick
  '#5F9EA0', // Cadet Blue
  '#D2691E', // Chocolate
  '#9ACD32', // Yellow Green
  '#CD5C5C', // Indian Red
  '#4169E1', // Royal Blue
  '#228B22', // Forest Green
  '#DA70D6', // Orchid
];

export const getDarkerColor = (color: string): string => {
  return chroma(color).darken(0.7).hex();
};

export const getWellColor = (index: number): string => {
  return WELL_COLORS[index % WELL_COLORS.length];
};

// Validation utilities
export const validateRadius = (value: string): boolean => {
  if (!value) return false;
  const numericValue = parseInt(value.replace(/,/g, ''), 10);
  return !isNaN(numericValue) && numericValue >= 5280 && numericValue <= 21120;
};

export const validateInclinationCutoff = (value: string): boolean => {
  const num = parseInt(value, 10);
  return !isNaN(num) && num >= 75 && num <= 90;
};

export const validateStepFrequency = (value: string): boolean => {
  const num = parseInt(value, 10);
  return !isNaN(num) && num >= 10 && num <= 50;
};

export const validateMaxThickness = (value: string): boolean => {
  const num = parseInt(value, 10);
  return !isNaN(num) && num >= 100;
};

export interface LandingAnalysisSettingsValues {
  usePerforations: boolean;
  inclinationCutoff: string;
  stepFrequency: string;
  maxThickness: string;
  calculateOffsetWells: boolean;
}

export const DEFAULT_LANDING_ANALYSIS_SETTINGS: LandingAnalysisSettingsValues =
  {
    usePerforations: false,
    inclinationCutoff: '80',
    stepFrequency: '30',
    maxThickness: '2000',
    calculateOffsetWells: false,
  };

// Data transformation utilities
export const transformWellData = (
  apiData: Well3DViewerResponse
): { wells: WellData[] } => {
  if (!apiData?.results?.length) {
    return { wells: [] };
  }

  const wellsWithBores = apiData.results.filter(
    (
      well
    ): well is typeof well & {
      wellBores: NonNullable<typeof well.wellBores>;
    } => Array.isArray(well.wellBores) && well.wellBores.length > 0
  );

  const allPoints = wellsWithBores.flatMap((well) =>
    well.wellBores.flatMap((bore) => bore.points)
  );

  if (allPoints.length === 0) return { wells: [] };

  const wells = wellsWithBores.map((well, wellIndex) => ({
    uwi: well.uwi,
    name: well.name ?? null,
    source: well.source ?? null,
    geometrySource: well.geometrySource || '',
    color: well.color || getWellColor(wellIndex),
    currentOperator: well.currentOperator || '',
    status: well.status || '',
    holeDirection: well.holeDirection ?? null,
    wellBores: well.wellBores.map((bore, boreIndex) => {
      // Normalize BE bore kinds to the internal types the viewer styles.
      // Estimated (synthesized vertical) geometry is detected via the `estimated`
      // flag (not `type` — the BE carries the bore-class there for every bore,
      // estimated included) and rendered distinctly (thinner + semi-transparent).
      // Pre-drill (planned) maps to `pre_drill` (dashed, default-visible).
      const isEstimated = Boolean(bore.estimated);
      const type =
        bore.type === 'Pre-Drill' || bore.geometrySource === 'pre_drill_auto'
          ? 'pre_drill'
          : bore.type;
      return {
        // Selection/checkbox key. Include (wellIndex, boreIndex) so a drilled
        // bore and a same-uwi14 pre-drill never collide into one id.
        id: `${bore.uwi14}:${type}:${isEstimated}:${wellIndex}:${boreIndex}`,
        uwi14: bore.uwi14,
        uwi12: bore.uwi12,
        type,
        estimated: isEstimated,
        spudDate: bore.spudDate ?? well.spudDate ?? null,
        geometryLabel: bore.geometryLabel,
        trajectory: bore.points.map((point) => ({
          x: point.x,
          y: point.y,
          z: point.z,
        })),
        profile2D: bore.profile2D,
        gunBarrelView: bore.gunBarrelView,
        workspace: bore.workspace ?? null,
      };
    }),
  }));

  return { wells };
};

// Utility function to find grid metadata by gridId
export const findGridMetadata = (
  collections: StructureGridCollection[],
  gridId: number,
  individualGrids: Grid[]
): {
  displayName: string;
  source: string;
  geologicInterval: string;
  displayColor: string;
} => {
  for (const collection of collections) {
    for (const grid of collection.grids) {
      if (grid.gridId === gridId) {
        return {
          displayName: grid.gridSurface || `Structure Grid ${gridId}`,
          source: grid.source || 'Unknown',
          geologicInterval: grid.geologicInterval || 'Unknown',
          displayColor: grid.displayColor,
        };
      }
    }
  }
  // If not found in collections, check individualGrids
  const individualGrid = individualGrids.find((grid) => grid.gridId === gridId);
  if (individualGrid) {
    return {
      displayName: individualGrid.gridSurface || `Structure Grid ${gridId}`,
      source: individualGrid.source || 'Unknown',
      geologicInterval: individualGrid.geologicInterval || 'Unknown',
      displayColor: individualGrid.displayColor,
    };
  }
  return {
    displayName: `Structure Grid ${gridId}`,
    source: 'Unknown',
    geologicInterval: 'Unknown',
    displayColor: '#FF5733',
  };
};

// Vertical exaggeration grid utilities
// Determines Z-axis tick spacing based on VE level:
//   1x–3x  → 6 divisions, labels rounded to nearest 500 ft
//   3.1x–7x → 12 divisions, labels rounded to nearest 100 ft
//   7x–10x → 24 divisions, labels rounded to nearest 25 ft
export const computeZTickLength = (ve: number, zRange: number): number => {
  const [numDivisions, roundTo] =
    ve <= 3 ? [6, 500] : ve <= 7 ? [12, 100] : [24, 25];
  const raw = zRange / numDivisions;
  return Math.max(Math.round(raw / roundTo) * roundTo, roundTo);
};

// GeoTIFF affine transformation utilities
export interface AffineTransformData {
  originX: number;
  originY: number;
  scaleX: number;
  scaleY: number;
  noDataValue: number;
  width: number;
  height: number;
}

export const extractAffineTransformData = (
  tiffImage: any
): AffineTransformData => {
  const [originX, originY] = tiffImage.getOrigin();
  const [scaleX, scaleY] = tiffImage.getResolution();
  const noDataValue = tiffImage.getGDALNoData();
  const width = tiffImage.getWidth();
  const height = tiffImage.getHeight();

  return {
    originX,
    originY,
    scaleX,
    scaleY,
    noDataValue,
    width,
    height,
  };
};

export const convertAffineToFeet = (
  originX: number,
  originY: number,
  scaleX: number,
  scaleY: number,
  col: number,
  row: number
): { xFeet: number; yFeet: number } => {
  const x = originX + col * scaleX;
  const y = originY - row * Math.abs(scaleY);
  const xFeet = x * 3.28084;
  const yFeet = y * 3.28084;

  return { xFeet, yFeet };
};
